import { useEffect, useState } from "react";
import { clientsApi, poolApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { LockedFeature } from "../../components/common/LockedFeature";

const FREQUENCIES = ["Weekly", "Biweekly", "Monthly", "Quarterly"];

export function RecurringOrdersPage() {
  const { notify } = useToast();
  const { hasFeature } = useFeatureTheme();
  const [orders, setOrders] = useState(null);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  // Bottle sizes, so an order can say what it is FOR. `quantity` was a
  // bare number with no unit -- twelve of what? -- which is why nothing
  // downstream could use it (#253).
  const [sizes, setSizes] = useState([]);
  const [form, setForm] = useState({
    client_id: "", frequency: "Monthly",
    next_delivery_date: new Date().toISOString().slice(0, 10),
    quantity: "", bottle_size_id: "", unit_price: "",
  });
  const [delivering, setDelivering] = useState(null);
  // Only asked for when the order has no price of its own. An assignment's
  // unit_price is NOT NULL, so a delivery genuinely cannot be valued
  // without one -- and a free delivery on a vendor's balance is worse than
  // one extra field.
  const [deliverPrice, setDeliverPrice] = useState("");

  /*
   * Not fetched at all without the feature (#258).
   *
   * Both calls are gated BusinessAdvanced server-side, so an account
   * without it got two 403s -- and because this was a bare Promise.all
   * with no catch, the rejection killed the whole load and left `clients`
   * empty. That is the blank Vendor dropdown that was reported: not a
   * lookup failure, a request that was never allowed to succeed.
   *
   * allSettled so one failing call cannot take the other with it, matching
   * #217's rule and #242's fix on the dashboard.
   */
  const enabled = hasFeature("BusinessAdvanced");

  async function load() {
    if (!enabled) return;
    const [ordersRes, clientsRes, sizesRes] = await Promise.allSettled([
      clientsApi.listRecurringOrders(),
      clientsApi.list(),
      // Also gated, and allSettled for the same reason as the other two.
      poolApi.listSizes(),
    ]);
    setOrders(ordersRes.status === "fulfilled" ? ordersRes.value.recurring_orders : []);
    setClients(clientsRes.status === "fulfilled" ? clientsRes.value.clients : []);
    setSizes(sizesRes.status === "fulfilled" ? sizesRes.value.bottle_sizes || [] : []);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [enabled]);

  async function submit() {
    if (!form.client_id || !form.quantity) return notify("Pick a vendor and a quantity", "warning");
    try {
      await clientsApi.createRecurringOrder({
        client_id: Number(form.client_id),
        frequency: form.frequency,
        next_delivery_date: form.next_delivery_date,
        quantity: Number(form.quantity),
        // Empty means bulk, and quantity is pounds -- the same either/or a
        // delivery uses, so fulfilment is a copy rather than a translation.
        bottle_size_id: form.bottle_size_id ? Number(form.bottle_size_id) : undefined,
        unit_price: form.unit_price ? Number(form.unit_price) : undefined,
      });
      notify("Recurring order created", "success");
      setOpen(false);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not create recurring order", "danger");
    }
  }

  /*
   * Delivering the order (#253).
   *
   * last_fulfilled has existed since the table was created and nothing
   * ever wrote to it: there was no way to say a delivery happened, so the
   * date never moved and the vendor's account never heard about it.
   *
   * This writes an ordinary honey assignment, so it lands on the vendor
   * ledger like any other delivery -- the order is a reminder of a
   * delivery to make, not a second kind of delivery.
   */
  async function deliver(order) {
    if (order.unit_price == null && !deliverPrice) {
      return notify("This order has no price — enter what it is worth", "warning");
    }
    try {
      const r = await clientsApi.fulfilRecurringOrder(order.order_id, {
        unit_price: deliverPrice ? Number(deliverPrice) : undefined,
      });
      notify(`Delivered — next due ${new Date(r.next_delivery_date).toLocaleDateString()}`, "success");
      setDelivering(null);
      setDeliverPrice("");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not record the delivery", "danger");
    }
  }

  async function stop(order) {
    try {
      await clientsApi.stopRecurringOrder(order.order_id);
      // Stopped, not deleted: the deliveries it produced are real and stay
      // on the vendor's ledger.
      notify(`Standing order for ${order.client_name} stopped`, "success");
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not stop the order", "danger");
    }
  }

  return (
    <div>
      <PageHeader title="Recurring Orders">
        {/* INSIDE the gate. Offering this to everyone let an account
            without the feature fill in the whole form and collect a 403 on
            save -- the same bug SuppliersPage had, for the same reason. A
            form you can complete and cannot submit is worse than no
            button. */}
        {enabled && <Button onClick={() => setOpen(true)}>New Recurring Order</Button>}
      </PageHeader>
      <LockedFeature enabled={hasFeature("BusinessAdvanced")} featureLabel="Business Advanced" previewText="Set up standing orders for the vendors who take honey from you regularly.">
        <Card>
          <DataTable
            rows={orders}
            loading={!orders}
            rowKey="order_id"
            emptyTitle="No recurring orders yet"
            emptyDescription="A standing order repeats on a schedule, so a regular customer is set up once instead of every time."
            columns={[
              { key: "client_name", header: "Vendor" },
              { key: "frequency", header: "Frequency" },
              {
                // The unit, at last. A bare "12" could not be turned into a
                // delivery because nothing said twelve of what (#253).
                key: "quantity",
                header: "Each time",
                render: (r) => (r.bottle_size_id
                  ? `${Number(r.quantity)} x ${r.bottle_size_label}`
                  : `${Number(r.quantity)} lb bulk`),
              },
              {
                key: "next_delivery_date",
                header: "Next Delivery",
                // days_until_due comes from the server so web and mobile
                // cannot disagree about what "due" means, and neither has
                // to trust a device clock.
                render: (r) => (
                  <span>
                    {new Date(r.next_delivery_date).toLocaleDateString()}{" "}
                    {r.days_until_due < 0 ? <Badge tone="danger">Overdue</Badge>
                      : r.days_until_due <= 7 ? <Badge tone="warning">Due soon</Badge> : null}
                  </span>
                ),
              },
              { key: "last_fulfilled", header: "Last delivered", render: (r) => (r.last_fulfilled ? new Date(r.last_fulfilled).toLocaleDateString() : "Never") },
              {
                key: "actions",
                header: "",
                render: (r) => (
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button size="sm" onClick={() => { setDelivering(r); setDeliverPrice(""); }}>Deliver</Button>
                    <Button size="sm" variant="secondary" onClick={() => stop(r)}>Stop</Button>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </LockedFeature>
      <Modal open={open} onClose={() => setOpen(false)} title="New Recurring Order" footer={<Button onClick={submit}>Save</Button>}>
        {/* Four controls, none of which said what it was (#205, swept up by
            #196). The date picker and the frequency dropdown had no name at
            all, and "Quantity (lbs)" was a placeholder -- gone as soon as you
            typed a number into it. */}
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="Vendor" required>
            {(id, aria) => (
              <select id={id} {...aria} value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} className="touch-target">
                <option value="">Choose customer…</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            )}
          </Field>
          <Field label="How often">
            {(id, aria) => (
              <select id={id} {...aria} value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className="touch-target">
                {FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
              </select>
            )}
          </Field>
          <Field label="Next delivery">
            {(id, aria) => <input id={id} {...aria} type="date" value={form.next_delivery_date} onChange={(e) => setForm((f) => ({ ...f, next_delivery_date: e.target.value }))} className="touch-target" />}
          </Field>
          {/* What the order is FOR. Without it "12" cannot become a
              delivery, which is why nothing downstream could use these
              rows (#253). */}
          <Field label="Jars or bulk">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={form.bottle_size_id}
                onChange={(e) => setForm((f) => ({ ...f, bottle_size_id: e.target.value }))}>
                <option value="">Bulk, by the pound</option>
                {sizes.map((sz) => (
                  <option key={sz.bottle_size_id} value={sz.bottle_size_id}>{sz.label}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="How many each time" hint={form.bottle_size_id ? "Number of jars." : "Pounds of bulk honey."} required>
            {(id, aria) => <input id={id} {...aria} type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className="touch-target" />}
          </Field>
          {/* Optional. NULL means never agreed, which is a real state and
              different from agreed to be nothing -- a delivery then gets
              recorded without a price rather than with a made-up one. */}
          <Field label="Price each (optional)">
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0" value={form.unit_price}
              onChange={(e) => setForm((f) => ({ ...f, unit_price: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={!!delivering} onClose={() => setDelivering(null)} title="Record this delivery"
        footer={<Button onClick={() => deliver(delivering)}>Deliver</Button>}>
        <p style={{ marginTop: 0 }}>
          {delivering && (delivering.bottle_size_id
            ? `${Number(delivering.quantity)} x ${delivering.bottle_size_label}`
            : `${Number(delivering.quantity)} lb bulk`)}{" "}
          to <strong>{delivering?.client_name}</strong>.
        </p>
        {delivering && delivering.unit_price == null && (
          <Field label="Price each" required
                 hint="This order was set up without one, and a delivery has to be worth something.">
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0"
              value={deliverPrice} onChange={(e) => setDeliverPrice(e.target.value)}
              className="touch-target" />}
          </Field>
        )}
        <p style={{ marginBottom: 0, color: "var(--color-text-muted)" }}>
          This goes onto their account like any other delivery, and the order
          rolls forward to its next date.
        </p>
      </Modal>
    </div>
  );
}
